import {
  HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,
} from '@angular/common/http';
import { inject, Inject, Injectable, InjectionToken } from '@angular/core';

import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, from, Observable, of, throwError, TimeoutError, timer } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { ToastService } from 'src/app/core/services/toast.service';
import { ApiloaderService } from 'src/app/shared/loaders/apiloader/apiloader.service';
import { LoadSpinnerService } from 'src/app/shared/loaders/spinner/loadspinner.service';
import { environment } from 'src/environments/environment';

import { CachingStrategyService } from '../caching';
import { ErrorhanderService } from '../services/error-handler.service';

export const TRACKREQ_ENABLED = new InjectionToken<boolean>('overridectx');
export const OVERRIDE_CTX = new HttpContextToken<boolean>(() => false);

/**
 * List of endpoints that use POST for retrieval and are safe to retry.
 * Developers can add new endpoints here to globally enable retries,
 * or use the RETRY_ALLOWED context in their service calls.
 */
const SAFE_SEARCH_URLS = [
  '/auth/realms/LMS'
];

/**
 * Token to explicitly allow retrying a non-idempotent request (POST/PUT/DELETE)
 * after a 401 refresh. Use this in services for search-like POSTs.
 *
 * This token is automatically enabled for URLs defined in SAFE_SEARCH_URLS.
 * Usage: this.http.post(url, body, { context: new HttpContext().set(RETRY_ALLOWED, true) })
 */
export const RETRY_ALLOWED = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private _errorService = inject(ErrorhanderService);
  private _loaderService = inject(LoadSpinnerService);
  private _oauthService = inject(OAuthService);
  private _apiLoaderService = inject(ApiloaderService);

  // Track if we're currently refreshing the token
  private isRefreshing = false;
  // Using undefined for "waiting", null for "error", and string for "success"
  private refreshTokenSubject = new BehaviorSubject<string | null | undefined>(undefined);

  constructor(private strategy: CachingStrategyService, private _toastrService: ToastService,
    @Inject(TRACKREQ_ENABLED) protected track_Request: boolean) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ignoreHndl = req.context.get(OVERRIDE_CTX);
    if (this.strategy.isCacheable(req)) {
      console.info(`Tracking[${ignoreHndl && this.track_Request}]`, req.url);
    }

    // Automatically allow retries for known safe search POST endpoints
    // We use a more specific check to ensure we only match the path part of the URL
    const isSafePost = req.method === 'POST' && SAFE_SEARCH_URLS.some(safePath => {
      return req.url.toLowerCase().includes((safePath as any).toLowerCase());
    });

    if (isSafePost) {
      req = req.clone({ context: req.context.set(RETRY_ALLOWED, true) });
    }

    return next.handle(req).pipe(
      catchError((error: any): Observable<HttpEvent<any>> => {
        return this.handleError(error, req, next, ignoreHndl) as Observable<HttpEvent<any>>;
      }),
    );
  }

  private handleError(
    returnedError: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler,
    ignoreHndl: boolean
  ): any {
    const errorService = this._errorService;
    const loaderService = this._loaderService;
    const handled: boolean = false;

    if (!handled && returnedError instanceof Error && !request.url?.endsWith('doLoanCreate')) {
      console.error('ABNORMAL Error: ', returnedError.message);
      errorService.handle(returnedError, `ABNORMAL Error`, true);
      // of(error) this basically handles the error and resumes normal execution
      return throwError(() => returnedError);
    }

    // **  TIMEOUT ERROR - handled by timeout interceptor ** //
    if (!handled && returnedError instanceof TimeoutError) {
      return throwError(() => returnedError);
    }

    if (!handled && returnedError instanceof HttpErrorResponse) {
      // If ignoreHndl is true (e.g. is-available), don't trigger the complex 401 refresh flow
      if(returnedError.status === 401 && !ignoreHndl) {
        const isIdempotent = ['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(request.method);
        const retryExplicitlyAllowed = request.context.get(RETRY_ALLOWED);

        // If the request is potentially dangerous and not explicitly opted-in, do NOT retry.
        if (!isIdempotent && !retryExplicitlyAllowed) {
          console.warn(`🛡️ 401 on non-idempotent ${request.method}. Blocking auto-retry`, { url: request.url });

          // We still trigger a background refresh so the USER'S NEXT action works,
          // but we let THIS specific request fail to avoid duplication.
          this.refreshTokenForFutureRequests();

          return throwError(() => returnedError);
        }

        // Attempt to refresh the token and retry the request
        return timer(500).pipe(
          switchMap(() => this.handle401Error(request, next))
        );
      }


      let errorMessage = '';
      // **  ERROR EVENT ** //
      if (returnedError.error) {
        errorMessage = `Error: ${returnedError.message || returnedError.error.message}`;
        console.error(errorMessage);
        const newerror = new Error(errorMessage);
        errorService.handle(newerror, `Response Error`, true);
        loaderService.stopLoading();
        return throwError(() => returnedError);
        //of(returnedError);
      }


      // **  HTTP RESPONSE ERROR ** //
      // **  PROGRESS ERROR ** //
      if (returnedError.status == 0 && returnedError.error instanceof ProgressEvent) {
        if (!returnedError.url?.endsWith('doLoanCreate')) {
          const message1 = returnedError.url ?? '';
          const hdrString1 = `Network error`;
          const showdateStr1 = new Date().toLocaleString();

          this._toastrService.error(`${showdateStr1} : ${message1}`, hdrString1, {
            positionClass: 'toast-top-right',
            closeButton: true
          });
          loaderService.stopLoading();
          return throwError(() => returnedError);
        }
      }

      const newError = new Error();
      switch (returnedError.status) {
        case 302: // Found REsponse
          newError.message = 'Duplicate Record Found!';
          errorService.handle(newError, `LMS Server Duplicate not allowed!!`, true);
          return throwError(() => newError);
          break;
        case 417:
          // NUPAY Error response
          console.error('LMS SERVER ::', returnedError.error.message);
          newError.message = returnedError?.error!.message || returnedError.message;

          errorService.handle(newError, `LMS Server [response]`, true);
          return throwError(() => returnedError);
          break;
        case 501:
          errorService.handleOffline(returnedError?.error!.message || 'Server Not Responding', 'Server OFFLINE');
          return throwError(() => returnedError);
          break;
        case 400: // Bad Request

          this._toastrService.error(
            `${new Date().toLocaleString()} Server response:<br><br>${returnedError.error!.message ?? ''}`,
            'Bad Request error',
            {
              enableHtml: true,
              positionClass: 'toast-top-right',
              timeOut: 18000,
              tapToDismiss: true,
            }
          );
          loaderService.stopLoading();
          return throwError(() => returnedError);
          //handled = handleBadRequest(returnedError);
          break;
        case 504: // Bad Gateway
          const message1 = returnedError.message || returnedError.error?.message || '';

          this._toastrService.error(
            `${new Date().toLocaleString()} Server response:<br><br>${message1 || ''}`,
            'Bad Request error',
            {
              enableHtml: true,
              positionClass: 'toast-top-right',
              timeOut: 18000,
              tapToDismiss: true,
            }
          );
          loaderService.stopLoading();
          return throwError(() => returnedError);
          ///handled = handleBadGateway(returnedError);
          break;
        default:
          if (returnedError.error && returnedError.error == 'Unauthorized' && returnedError.status == 401) {
            errorService.handleString('Unauthorized recieved. Require login');
          } else if (
            returnedError.error &&
            returnedError.error.type == 'error' &&
            returnedError.statusText === 'Unknown Error'
          ) {
            //Offline
            errorService.handleOffline(returnedError.message || returnedError.error.message, 'Server OFFLINE');
          } else {
            if (returnedError.error && !returnedError.error!.result) {
              errorService.handleServerSideError(returnedError);
              break;
            }

            if (
              (returnedError.error?.trace || returnedError.error?.stackTrace) &&
              !environment?.production &&
              !!environment?.showtrace
            ) {
              errorService.handleStack(returnedError.error?.trace || returnedError.error?.stackTrace);
            }
          }
          if (
            (returnedError.error?.trace || returnedError.error?.stackTrace) &&
            !environment?.production &&
            !!environment?.showtrace
          ) {
            errorService.handleStack(returnedError.error?.trace || returnedError.error?.stackTrace);
          }
          break;
      }
      if (handled) {
        return of(returnedError);
      }
    }

    if (!handled) {
      return throwError(() => returnedError);
    }

    return of(returnedError);
  }

  private refreshTokenForFutureRequests(): void {
    if (this.isRefreshing) {
      return;
    }

    const hasRefreshToken = !!this._oauthService.getRefreshToken();
    if (!hasRefreshToken) {
      this._oauthService.revokeTokenAndLogout(true);
      return;
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(undefined);

    from(this._oauthService.refreshToken())
      .pipe(
        switchMap(() => {
          const newToken = this._oauthService.getAccessToken();
          if (!newToken) {
            throw new Error('Authentication failed: Empty refreshed access token');
          }

          this.refreshTokenSubject.next(newToken);
          return of(newToken);
        }),
        catchError((error) => {
          console.error('Background token refresh failed', error);
          this.refreshTokenSubject.next(null);
          this._oauthService.revokeTokenAndLogout(true);
          return of(null);
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      )
      .subscribe();
  }

  /**
   * Handle 401 errors by attempting to refresh the access token
   * If refresh succeeds, retry the original request with the new token
   * If refresh fails, force logout and redirect to login
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const requestId = request.headers.get('X-Debug-Request-Id');

    // 1. PRE-FLIGHT GUARD: Do we even have a refresh token to use?
    // Note: Adjust the getter method based on your specific OAuth library
    const hasRefreshToken = !!this._oauthService.getRefreshToken();

    if (!hasRefreshToken) {
      console.warn('⛔ 401 received but no refresh token exists. Forcing logout.', { requestId });
      this._oauthService.revokeTokenAndLogout(true);
      // Immediately terminate the stream with an error
      return throwError(() => new Error('Authentication failed: No refresh token'));
    }

    // 2. EARLY RETURN: If already refreshing, join the queue
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        // Ensure we only proceed when a valid token (not undefined) is emitted
        filter((token): token is string | null => token !== undefined),
        take(1),
        switchMap((token: string | null) => {
          if (!token) {
            return throwError(() => new Error('Authentication failed: Refresh rejected'));
          }

          console.info('🔁 Retrying queued request', { requestId, url: request.url });
          return next.handle(this.addToken(request, token));
        })
      );
    }

    // 2. MAIN PATH: We are the first to hit the 401, start the refresh process
    this.isRefreshing = true;
    this.refreshTokenSubject.next(undefined);
    console.log('🔄 Refreshing access', { requestId, url: request.url });

    // 3. FINAL RETURN: The compiler sees this as the guaranteed default return
    return from(this._oauthService.refreshToken()).pipe(
      switchMap(() => {
        // Refresh succeeded
        const newToken = this._oauthService.getAccessToken();
        if (!newToken) {
          this.refreshTokenSubject.next(null);
          this._oauthService.revokeTokenAndLogout(true);
          return throwError(() => new Error('Authentication failed: Empty refreshed access token'));
        }

        this.refreshTokenSubject.next(newToken);
        console.info('🔁 Retrying original request', { requestId, url: request.url });

        return next.handle(this.addToken(request, newToken));
      }),
      catchError((error) => {
        // Refresh failed
        console.error('Background token refresh failed', error);

        this.refreshTokenSubject.next(null);
        this._oauthService.revokeTokenAndLogout(true);
        return throwError(() => new Error('Authentication failed: Refresh rejected'));
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  /**
   * Handle 401 errors by attempting to refresh the access token
   * If refresh succeeds, retry the original request with the new token
   * If refresh fails, force logout and redirect to login
   */
  // private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  //   const requestId = request.headers.get('X-Debug-Request-Id');

  //   if (!this.isRefreshing) {
  //     this.isRefreshing = true;
  //     this.refreshTokenSubject.next(undefined);

  //     console.log('🔄 Refreshing access', {
  //       requestId,
  //       url: request.url,
  //     });

  //     const hasToken = this._oauthService.hasValidAccessToken();
  //     // Use an observable stream instead of .subscribe() to prevent leaks
  //     if(!hasToken) {
  //         const exp = this._oauthService.getAccessTokenExpiration();
  //         if(!exp) {
  //           this._oauthService.revokeTokenAndLogout(true);
  //           return this.refreshTokenSubject.pipe(
  //             filter(token => token !== undefined),
  //             take(1),
  //             switchMap(token => {
  //               if (!token) return throwError(() => new Error('Authentication failed'));

  //               const clonedRequest = this.addToken(request, token);
  //               console.info('🔁 Retrying request', { requestId, url: request.url });

  //               return timer(800).pipe(
  //                 switchMap(() => next.handle(clonedRequest))
  //               );
  //             })
  //           );
  //         }
  //         const now = new Date().getTime();
  //         if (exp > now && exp - now < 5 * 60 * 1000) {
  //           console.log('Token is expiring soon, attempting background refresh');
  //           this._oauthService.refreshToken().catch(() => {
  //             console.error('Background token refresh failed');
  //             //oauthService.logOut(true);
  //             //return of(false).toPromise();
  //             return false;
  //           }).then(() => {
  //             console.log('Background token refresh successful');
  //             //return of(true).toPromise();
  //             return true;
  //           });
  //         }
  //       }
  //       // this._oauthService.silentRefresh().then((value) => {
  //       //   console.log('✅ Token refresh');
  //       //   this.refreshTokenSubject.next(this._oauthService.getAccessToken());
  //       // })
  //       // .finally(() => {
  //       //   this._oauthService.showDebugInformation = true;
  //       //   this._oauthService.checkSession();
  //       //   this.isRefreshing = false
  //       // })
  //       // .catch((reson) => {
  //       //   if(reson === 'silent_refresh_timeout') {
  //       //     this._loaderService.stopLoading();
  //       //     this._toastrService.info('Session expired. attempt refresh...', 'refresh attempt');
  //       //     setTimeout(() => {
  //       //       this._oauthService.showDebugInformation = true;
  //       //       this._oauthService.loadDiscoveryDocumentAndTryLogin({
  //       //         onTokenReceived: (receivedTokens) =>{
  //       //           this.refreshTokenSubject.next(this._oauthService.getAccessToken());
  //       //         }
  //       //       });
  //       //     }, 2000);
  //       //   }
  //       //   console.error('❌ Token refresh ', reson);
  //       //       this.refreshTokenSubject.next(null);
  //       //       this._loaderService.stopLoading();
  //       //       this._oauthService.revokeTokenAndLogout(true);
  //       //       this._toastrService.error('Session expired. Redirecting...', 'Auth Error');
  //       //       setTimeout(() => { window.location.href = window.location.origin; }, 2000);
  //       // });
  //     //}
  //     // from(this._oauthService.getRefreshToken()).pipe(
  //     //   take(1),
  //     //   finalize(() => this.isRefreshing = false)
  //     // ).subscribe({
  //     //     next: () => {
  //     //       console.log('✅ Token refresh');
  //     //       this.refreshTokenSubject.next(this._oauthService.getAccessToken());
  //     //     },
  //     //     error: (err) => {

  //     //     }
  //     //   });
  //   }

  //   // Both leader and followers wait for the subject.
  //   // If the component is destroyed, RxJS will naturally cancel this subscription.
  //   return this.refreshTokenSubject.pipe(
  //     filter(token => token !== undefined),
  //     take(1),
  //     switchMap(token => {
  //       if (!token) return throwError(() => new Error('Authentication failed'));

  //       const clonedRequest = this.addToken(request, token);
  //       console.info('🔁 Retrying request', { requestId, url: request.url });

  //       return timer(800).pipe(
  //         switchMap(() => next.handle(clonedRequest))
  //       );
  //     })
  //   );
  // }

  /**
   * Add the access token to the request headers
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
